import { API_BASE_URL as API_URL } from '../config';

export const authService = {
  async login(email, password) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    
    const data = await response.json();
    sessionStorage.setItem('signalcue_token', data.access_token);
    sessionStorage.setItem('signalcue_user', JSON.stringify(data.user));
    return data;
  },

  async logout() {
    const token = sessionStorage.getItem('signalcue_token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    sessionStorage.clear();
  },

  async changePassword(newPassword) {
    const token = sessionStorage.getItem('signalcue_token');
    const response = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ new_password: newPassword })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password change failed');
    }
    
    return response.json();
  },

  async getCurrentUser() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) return null;

    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      sessionStorage.clear();
      return null;
    }

    return response.json();
  },

  getToken() {
    return sessionStorage.getItem('signalcue_token');
  },

  getUser() {
    const userStr = sessionStorage.getItem('signalcue_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated() {
    return !!this.getToken();
  },

  // Organisation management methods (Step 3)
  async getOrganisation() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/organisation`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Organisation not found - return null instead of throwing
        return null;
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get organisation details');
    }

    return response.json();
  },

  async updateOrganisation(updates) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/organisation`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update organisation');
    }

    return response.json();
  },

  async getOrganisationStatus() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/organisation/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Return default status for new organisations
        return {
          setup_status: 'not_configured',
          progress_percentage: 0,
          completed_steps: [],
          pending_steps: [
            'Configure organisation details',
            'Add team members',
            'Configure territories and sectors',
            'Define recruiter specialisations'
          ]
        };
      }
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get organisation status');
    }

    return response.json();
  },

  async getPipelineRuns(limit = 20, tenantId = null) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    if (tenantId) params.set('tenant_id', tenantId);
    const url = `${API_URL}/api/admin/pipeline-runs${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get pipeline runs');
    }
    return response.json();
  },

  async getScoringConfig() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_URL}/api/admin/scoring-config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get scoring config');
    }
    return response.json();
  },

  async updateScoringConfig(configJson) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(`${API_URL}/api/admin/scoring-config`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ config_json: configJson })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save scoring config');
    }
    return response.json();
  },

  // Step 4 - Setup Wizard methods
  async getSetupWizardState() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/state`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get wizard state');
    }

    return response.json();
  },

  async addTeamMember(memberData) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/team-members`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(memberData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to add team member');
    }

    return response.json();
  },

  async deleteTeamMember(userId) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/team-members/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete team member');
    }

    return response.json();
  },

  async getTeamMembers() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/team-members`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get team members');
    }

    return response.json();
  },

  async assignManager(recruiterUserId, managerUserId) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/assign-manager`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        recruiter_user_id: recruiterUserId,
        manager_user_id: managerUserId
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to assign manager');
    }

    return response.json();
  },

  async getManagers() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/managers`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get managers');
    }

    return response.json();
  },

  async getRecruiters() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/recruiters`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get recruiters');
    }

    return response.json();
  },

  async getRecruitersAndManagers() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/recruiters-and-managers`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get recruiters and managers');
    }

    return response.json();
  },

  async getTerritories() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/territories`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get territories');
    }

    return response.json();
  },

  async createTerritory(name) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/territories`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create territory');
    }

    return response.json();
  },

  async getSectors() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/sectors`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get sectors');
    }

    return response.json();
  },

  async createSector(name) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/sectors`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create sector');
    }

    return response.json();
  },

  async getRoleFamilies() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/role-families`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get role families');
    }

    return response.json();
  },

  async createRoleFamily(name) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/role-families`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ name })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create role family');
    }

    return response.json();
  },

  async defineRecruiterSpecialisation(recruiterUserId, specialisationData) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/recruiter-specialisation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        recruiter_user_id: recruiterUserId,
        ...specialisationData
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to define specialisation');
    }

    return response.json();
  },

  async getRecruiterSpecialisation(recruiterUserId) {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/recruiter-specialisation/${recruiterUserId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get specialisation');
    }

    return response.json();
  },

  async getSetupReview() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/review`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get setup review');
    }

    return response.json();
  },

  async completeSetup() {
    const token = sessionStorage.getItem('signalcue_token');
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_URL}/api/admin/setup-wizard/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ confirm: true })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to complete setup');
    }

    return response.json();
  },

  // Password setup with invitation token
  async validateInvitationToken(token) {
    const response = await fetch(`${API_URL}/api/public/setup-password/validate?token=${encodeURIComponent(token)}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Invalid invitation token');
    }

    return response.json();
  },

  async setupPassword(token, password) {
    const response = await fetch(`${API_URL}/api/public/setup-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to set password');
    }

    return response.json();
  }
};